/**
 * Nexera Pricing Config
 *
 * `TAX_RATE` below is the FALLBACK DEFAULT tax rate, used by the hooks in
 * src/hooks/useTaxRate.ts whenever the per-organization rate — the actual source
 * of truth, stored server-side and fetched from the backend — is loading or
 * unavailable. Cart and Checkout should not import this constant directly.
 *
 * **Cart and Checkout must use `useCartTax`**, which resolves a rate per item from
 * that item's own organization. Do NOT reach for `useTaxRate`/`useTaxRateValue`
 * there: they resolve a single rate, and a storefront cart may legitimately span
 * organizations on different rates — assuming one rate for the whole cart is the
 * defect R-033 and this module's history are about.
 *
 * That restriction is about TAX_RATE only — `formatTaxRatePercent` below is a pure
 * display helper and is meant to be imported wherever a rate is rendered.
 */

// =============================================================================
// EDITABLE PRICING CONSTANTS
// =============================================================================

/** Algeria standard VAT rate (19%) — the fallback default when the backend rate is unavailable. */
export const TAX_RATE = 0.19;

/**
 * Render a tax rate (a fraction, e.g. 0.125) as a percentage label, e.g. "12.5".
 *
 * The label MUST be derived from the same value the adjacent amount is computed
 * from. Rates are per-organization and arbitrary to two decimal places, so a
 * rounded label (`toFixed(0)`) would state one percentage next to an amount
 * charged at another — the exact defect class R-019 was filed against.
 * Trailing zeros are trimmed so 0.19 renders "19", not "19.00".
 */
export const formatTaxRatePercent = (taxRate: number): string =>
  String(Number((taxRate * 100).toFixed(2)));
